import type { GetRowIdParams } from 'ag-grid-community';
import { BlotterGridDispatchProps, BlotterGridStateProps } from 'components/shared/Blotter/Grid/blotter.model';
import { DlpProductTradeId } from 'epic/blotters/commonBlotterEpics';
import { useAppSelector } from 'hooks/useAppSelector';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { IBlotterItem } from 'models/IBlotterItem';
import { useCallback } from 'react';
import { blotterPanelItemReset } from 'state/blotters/blotterPanel/blotterPanel.action';
import { blotterPanelItemDetailsRequestedThunk } from 'state/blotters/blotterPanel/blotterPanel.thunks';
import { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import {
  tradesBlottersColumnStateChanged,
  tradesBlottersSortChanged,
} from 'state/blotters/trades/tradesBlotters.actions';
import {
  selectTradesBlottersColumnDefinition,
  selectTradesBlottersPreferenceState,
} from 'state/blotters/trades/tradesBlotters.selector';
import { TradesBlottersItemByBlotterType } from 'state/blotters/trades/types/blotterItem';
import { BlotterType, PreferenceBlotterType } from 'state/blotters/trades/types/types';

type OwnProps<T extends PreferenceBlotterType> = {
  blotter: T;
  idKey: keyof TradesBlottersItemByBlotterType<T>;
  hasSidePanel?: boolean;
};
type StateProps<T extends BlotterType> = Omit<BlotterGridStateProps<TradesBlottersItemByBlotterType<T>>, 'items'> & {
  getRowId: (p: GetRowIdParams) => string;
};
type DispatchProps<T extends BlotterType> = BlotterGridDispatchProps<TradesBlottersItemByBlotterType<T>>;

export function useCommonConnect<T extends PreferenceBlotterType>({
  blotter,
  idKey,
  hasSidePanel = true,
}: OwnProps<T>): StateProps<T> & DispatchProps<T> {
  const { pageSizeState, columnGroupState, columnState } = useAppSelector(state =>
    selectTradesBlottersPreferenceState(state, blotter),
  );
  const columnDefinitions = useAppSelector(state =>
    selectTradesBlottersColumnDefinition(state, blotter),
  ) as IBlotterColumnDefinition<TradesBlottersItemByBlotterType<T>>[];

  const getRowId = useCallback(
    (p: GetRowIdParams) => {
      return p.data[idKey];
    },
    [idKey],
  );

  const dispatch = useThunkDispatch();

  const onBlotterColumnStateChanged = useCallback(
    (columnState: readonly any[]) => {
      dispatch(tradesBlottersColumnStateChanged(blotter, columnState));
    },
    [dispatch, blotter],
  );

  const onColumnSortChanged: DispatchProps<T>['onColumnSortChanged'] = useCallback(
    (sort: string | null, sortIndex: 'asc' | 'desc' | null) => {
      dispatch(tradesBlottersSortChanged(blotter, sort, sortIndex));
    },
    [dispatch, blotter],
  );

  const onSetActiveItem = useCallback(
    (item?: TradesBlottersItemByBlotterType<T>) => {
      if (hasSidePanel) {
        dispatch(
          blotterPanelItemDetailsRequestedThunk(
            item =>
              blotter === 'dlpProductTrades'
                ? ({ productIsin: item?.productIsin, tradeReference: item?.tradeReference } as DlpProductTradeId)
                : (item?.[idKey as keyof IBlotterItem] as string),
            item as IBlotterItem,
          ),
        );
      } else {
        dispatch(blotterPanelItemReset());
      }
    },
    [dispatch, blotter, idKey, hasSidePanel],
  );
  return {
    getRowId,
    columnDefinitions,
    pageSizeState,
    columnGroupState,
    columnState,
    onBlotterColumnStateChanged,
    onColumnSortChanged,
    onSetActiveItem,
  };
}
